
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

export function useTemplateEditorPage(id: string | undefined) {
  const navigate = useNavigate();
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [contentMounted, setContentMounted] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Check authentication status first
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        setIsAuthenticated(!!data.session);
      } catch (err) {
        console.error('Auth check failed:', err);
        setHasError(true);
        setErrorMessage('Failed to verify authentication. Please try logging in again.');
      } finally {
        setAuthChecked(true);
      }
    };
    
    checkAuth();
  }, []);
  
  // Handle component mounting with controlled timing
  useEffect(() => {
    // Skip if auth isn't checked yet
    if (!authChecked) return;
    
    // Show error if not authenticated
    if (!isAuthenticated) {
      setHasError(true);
      setErrorMessage('Authentication required. Please login again.');
      setIsLoaded(true);
      setIsAnimating(false);
      return;
    }
    
    // Use a single AbortController for all effects
    const abortController = new AbortController();
    
    // Immediately start animation
    setIsAnimating(true);
    
    // Clear error state when ID changes
    setHasError(false);
    setErrorMessage('');
    
    // Reset loaded state
    setIsLoaded(false);
    setContentMounted(false);
    
    // First, mark the component as loaded (shows skeleton)
    const initialTimer = setTimeout(() => {
      if (abortController.signal.aborted) return;
      
      setIsLoaded(true);
      
      // After a short delay, mount the actual content
      const mountTimer = setTimeout(() => {
        if (abortController.signal.aborted) return;
        
        setContentMounted(true);
        // End animation after content is mounted
        setTimeout(() => {
          if (abortController.signal.aborted) return;
          setIsAnimating(false);
        }, 300);
      }, 300);
    }, 100);
    
    return () => {
      abortController.abort();
      clearTimeout(initialTimer);
    };
  }, [id, authChecked, isAuthenticated]);
  
  const handleBack = useCallback(() => {
    // Start exit animation
    setIsAnimating(true);
    // Allow time for animation before navigating
    setTimeout(() => {
      // Navigate back to email templates
      navigate('/email-templates');
    }, 150);
  }, [navigate]);
  
  const handleSave = useCallback(() => {
    // After saving, we stay on the current page
  }, []);

  const handleRetry = useCallback(() => {
    setIsRetrying(true);
    setRetryCount(prev => prev + 1);
    setHasError(false);
    setErrorMessage('');
    setIsLoaded(false);
    setContentMounted(false);
    setIsAnimating(true);
    
    // Check auth again
    const checkAuth = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        setIsAuthenticated(!!data.session);
      } catch (err) {
        console.error('Auth check failed:', err);
        setHasError(true);
        setErrorMessage('Failed to verify authentication. Please try logging in again.');
      } finally {
        setAuthChecked(true);
      }
    };
    
    checkAuth();
    
    // Use exponential backoff for retries
    const backoffDelay = Math.min(1000 * Math.pow(1.5, retryCount), 5000);
    
    // Reset the loading sequence with delay
    setTimeout(() => {
      setIsLoaded(true);
      setTimeout(() => {
        setContentMounted(true);
        setIsRetrying(false);
        // End animation after content is mounted
        setTimeout(() => setIsAnimating(false), 300);
      }, 300);
    }, backoffDelay);
  }, [retryCount]);

  return {
    isLoaded,
    hasError,
    errorMessage,
    contentMounted,
    isRetrying,
    isAnimating,
    authChecked,
    isAuthenticated,
    handleBack,
    handleSave,
    handleRetry
  };
}
